import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection, AnimatedHeading } from "@/components/ui/AnimatedSection";
import { 
  Truck,
  Smartphone,
  Gift,
  CreditCard,
  Wifi,
  Package,
  Phone,
  Coins,
  CircleDollarSign,
  Ticket,
  UtensilsCrossed,
  Wine,
  Dices,
  PartyPopper
} from "lucide-react";
// Product images
import gemueseregalImg from "@/assets/gemueseregal-3.webp";
import obstregalImg from "@/assets/obstregal-3.webp";
import frischethekenImg from "@/assets/frischetheke-3.webp";
import baeckereiImg from "@/assets/baeckerei-3.webp";
import regionalesRegalImg from "@/assets/regionales-regal-3.webp";
import dhlPackstationImg from "@/assets/dhl-packstation-3.webp";
import { SEO } from "@/components/SEO";
const leitungFrischethekenImg = "/lovable-uploads/leitung-frischetheke-neu.webp";


const products = [
  {
    title: "Obst & Gemüse",
    description: "Täglich frisches Obst und Gemüse, viel davon aus der Region.",
    image: gemueseregalImg,
  },
  {
    title: "Freshcut & Salatbar",
    description: "Täglich frische, gesunde Salate und andere frische Produkte – alles jeden Tag frisch zubereitet.",
    image: obstregalImg,
  },
  {
    title: "Frischetheke & Fleischerei",
    description: "Hochwertige Fleisch- und Wurstwaren aus eigener Herstellung.",
    image: frischethekenImg,
  },
  {
    title: "Heiße Theke",
    description: "Leckere warme Speisen für den schnellen Hunger zwischendurch. Mit abwechselnden Menüs.",
    image: leitungFrischethekenImg,
  },
  {
    title: "Bäckerei Schäfer",
    description: "Frische Backwaren direkt am Markt – vom knusprigen Brötchen bis zum süßen Gebäck.",
    image: baeckereiImg,
  },
  {
    title: "Getränke & Regionales",
    description: "Große Auswahl an Getränken, darunter auch regionale Weine und Spezialitäten.",
    image: regionalesRegalImg,
  },
];

const services = [
  {
    icon: <Truck className="w-6 h-6" />,
    title: "Abholservice",
    description: "Bestelle online und hole deine Einkäufe fertig gepackt ab.",
  },
  {
    icon: <CircleDollarSign className="w-6 h-6" />,
    title: "REWE Bonus",
    description: "Sammle Punkte und profitiere von exklusiven Vorteilen.",
  },
  {
    icon: <Smartphone className="w-6 h-6" />,
    title: "REWE App",
    description: "Digitale Angebote, Einkaufslisten und mehr in einer App.",
  },
  {
    icon: <Gift className="w-6 h-6" />,
    title: "Geschenkservice",
    description: "Geschenkkörbe und individuelle Verpackungen auf Anfrage.",
  },
  {
    icon: <UtensilsCrossed className="w-6 h-6" />,
    title: "Partyservice",
    description: "Catering und Platten für deine Feier oder Veranstaltung.",
  },
  {
    icon: <Ticket className="w-6 h-6" />,
    title: "Loot und Gutscheinkarten",
    description: "Große Auswahl an Gutscheinkarten für Gaming, Streaming und mehr.",
  },
  {
    icon: <CreditCard className="w-6 h-6" />,
    title: "Bezahlmethoden",
    description: "Visa, Mastercard, EC-Karte und viele weitere Optionen.",
  },
  {
    icon: <Coins className="w-6 h-6" />,
    title: "Cashback",
    description: "Bargeldabhebung von 0,01 € bis 200 € möglich.",
  },
  {
    icon: <Package className="w-6 h-6" />,
    title: "DHL Paketshop",
    description: "Briefe und Pakete aufgeben und abholen.",
  },
  {
    icon: <Wifi className="w-6 h-6" />,
    title: "Gratis WLAN",
    description: "Kostenloses WLAN während deines Einkaufs.",
  },
  {
    icon: <Wine className="w-6 h-6" />,
    title: "Getränke vorkühlen",
    description: "Wir kühlen deine Getränke auf Wunsch vor.",
  },
  {
    icon: <Phone className="w-6 h-6" />,
    title: "Taxi-Ruf Service",
    description: "Wir rufen dir gerne ein Taxi.",
  },
  {
    icon: <Dices className="w-6 h-6" />,
    title: "Lotto",
    description: "Nutze gerne die Lottostation in unserem Markt.",
  },
  {
    icon: <PartyPopper className="w-6 h-6" />,
    title: "Heliumballons & Heliumgas",
    description: "Befüllte Heliumballons und Heliumgas für deine Feier – direkt bei uns erhältlich.",
  },
];

export default function ProdukteService() {
  return (
    <Layout>
      <SEO title="Produkte & Service | REWE V. Gahndi" description="Frischetheke, Bäckerei, Heiße Theke, Lotto, DHL Packstation, Heliumballons – alle Services im Überblick." path="/produkte-service" />
      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${frischethekenImg})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative container-custom py-20">
          <AnimatedHeading as="h1" subtitle="Produkte & Service">
            Unser <span className="text-gold-gradient">Sortiment</span>
          </AnimatedHeading>
          <AnimatedSection>
            <p className="text-center text-muted-foreground max-w-3xl mx-auto text-lg">
              Entdecke unser vielfältiges Angebot an frischen Produkten und praktischen Services.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Products with Images */}
      <section className="section-padding">
        <div className="container-custom">
          <AnimatedHeading subtitle="Frische & Qualität">
            Unsere <span className="text-gold-gradient">Abteilungen</span>
          </AnimatedHeading>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <AnimatedSection key={product.title} delay={index * 0.1}>
                <motion.div 
                  className="card-premium overflow-hidden group"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="aspect-video overflow-hidden rounded-xl mb-4">
                    <img 
                      src={product.image} 
                      alt={product.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-heading font-semibold mb-2">{product.title}</h3>
                    <p className="text-muted-foreground text-sm">{product.description}</p>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Frischetheke Highlight */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-2xl aspect-[4/3]"
              >
                <img 
                  src={frischethekenImg} 
                  alt="Frischetheke mit Fleisch- und Wurstwaren" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="text-center lg:text-left">
                <p className="text-primary uppercase tracking-widest text-sm font-medium mb-3">
                  Highlight
                </p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-6">
                  Unsere <span className="text-gold-gradient">Frischetheke</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Ganz besonders sticht unsere eigene Fleischerei heraus, in der viele hochwertige 
                  Produkte in eigener Herstellung produziert werden. Frische Wurst, zartes Fleisch 
                  und köstliche Aufschnitte – alles mit Liebe zubereitet.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Unser geschultes Team berät dich gerne und erfüllt auch individuelle Wünsche. 
                  Ob für den täglichen Bedarf oder das besondere Fest – bei uns findest du Qualität, 
                  die man schmeckt.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Bäckerei Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection className="order-2 lg:order-1">
              <div className="text-center lg:text-left">
                <p className="text-primary uppercase tracking-widest text-sm font-medium mb-3">
                  Im Markt
                </p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-6">
                  Bäckerei <span className="text-gold-gradient">Schäfer</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Direkt am Markt findest du die Bäckerei Schäfer mit frischen Backwaren – 
                  vom knusprigen Brötchen bis zum süßen Gebäck. Jeden Tag frisch gebacken 
                  für dich.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Ob zum Frühstück, zur Kaffeepause oder für den Abend – hier findest du 
                  immer etwas Leckeres. Und das Beste: Du kannst direkt beim Einkauf 
                  dein frisches Brot mitnehmen.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2} className="order-1 lg:order-2">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-2xl aspect-[4/3]"
              >
                <img 
                  src={baeckereiImg} 
                  alt="Bäckerei Schäfer mit frischen Backwaren" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>


      {/* Services Grid - Unified 3-column layout */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <AnimatedHeading subtitle="Für deinen Komfort">
            Unsere <span className="text-gold-gradient">Services</span>
          </AnimatedHeading>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <AnimatedSection key={service.title} delay={index * 0.05}>
                <div className="card-premium text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-heading font-semibold mb-2">{service.title}</h3>
                  <p className="text-muted-foreground text-sm">{service.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* DHL Packstation */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-2xl aspect-[4/3]"
              >
                <img 
                  src={dhlPackstationImg} 
                  alt="DHL Packstation vor dem REWE Markt" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="text-center lg:text-left">
                <p className="text-primary uppercase tracking-widest text-sm font-medium mb-3">
                  Service
                </p>
                <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-6">
                  DHL <span className="text-gold-gradient">Packstation</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Direkt vor unserem Markt findest du eine DHL Packstation. Hier kannst du 
                  rund um die Uhr deine Pakete abholen oder aufgeben – ganz bequem während 
                  deines Einkaufs.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Zusätzlich bieten wir im Markt einen DHL Paketshop an, wo du Briefe und 
                  Pakete aufgeben, frankieren und abholen kannst.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Abholservice CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <AnimatedSection>
            <div className="card-premium border-gold-glow text-center p-8 md:p-12 max-w-4xl mx-auto">
              <Truck className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="text-2xl md:text-3xl font-heading font-semibold mb-4">
                Der REWE <span className="text-gold-gradient">Abholservice</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                Keine Zeit zum Einkaufen? Kein Problem! Bestelle bequem von zu Hause aus 
                und hole deine fertig gepackten Einkäufe einfach bei uns ab. Spare Zeit 
                und genieße dennoch die volle Produktvielfalt unseres Marktes.
              </p>
              <a
                href="https://www.rewe.de/marktseite/floersheim-weilbach/240367/rewe-markt-industriestrasse-40/#abholservice"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold-filled"
              >
                Jetzt online bestellen
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
